import { Injectable, signal, computed } from '@angular/core';

export enum LoadingStates {
  UNKNOWN = "UNKNOWN",
  REGISTER = "REGISTER",
  LOGIN = "LOGIN",
  REQUEST_PASSWORD = "REQUEST_PASSWORD",
  RESTORE_PASSWORD = "RESTORE_PASSWORD",
  CATEGORIES_GET = "CATEGORIES_GET",
  MARKETPLACES_GET = "MARKETPLACES_GET",
  WORK_FORMATS_GET = "WORK_FORMATS_GET",
  PLATFORMS_GET = "PLATFORMS_GET",
  EDIT_USER_TYPE = "EDIT_USER_TYPE",
  VERIFICATION_STATUS_PENDING = "VERIFICATION_STATUS_PENDING",
  VERIFICATION_STATUS_DRAFT = "VERIFICATION_STATUS_DRAFT",
  CREATE_PAYOUT = "CREATE_PAYOUT",
  CHECK_PASSWORD_LINK = "CHECK_PASSWORD_LINK",
  BRIEFS_CREATE_EDIT = "BRIEFS_CREATE_EDIT",
  BRIEFS_DELETE = "BRIEFS_DELETE",
  BRIEFS_GET = "BRIEFS_GET",
  BRIEFS_GEO_GET = "BRIEFS_GEO_GET",
  BRIEFS_GET_ITEM = "BRIEFS_GET_ITEM",
  BRIEFS_STATUS_CHANGE = "BRIEFS_STATUS_CHANGE",
  BRIEFS_GET_LIST_OPPONENT = "BRIEFS_GET_LIST_OPPONENT",
  BRIEFS_GET_PAY = "BRIEFS_GET_PAY",
  BRIEFS_POST_PAY = "BRIEFS_POST_PAY",
  BRIEFS_UPDATE_INTEGRATIONS = "BRIEFS_UPDATE_INTEGRATIONS",
  INTEGRATIONS_CREATE_RATING = "INTEGRATIONS_CREATE_RATING",
  INTEGRATIONS_GET = "INTEGRATIONS_GET",
  INTEGRATIONS_CREATE = "INTEGRATIONS_CREATE",
  INTEGRATIONS_CANCEL = "INTEGRATIONS_CANCEL",
  INTEGRATIONS_FINISH = "INTEGRATIONS_FINISH",
  RESPONSE_STATUS_CHANGE = "RESPONSE_STATUS_CHANGE",
  BRIEFS_RECALL = "BRIEFS_RECALL",
  BLOGGER_GET = "BLOGGER_GET",
  REPORTS_CREATE = "REPORTS_CREATE",
  REPORTS_STATUS_CHANGE = "REPORTS_STATUS_CHANGE",
  REPORTS_GET = "REPORTS_GET",
  REPORTS_DOWNLOAD_FILE = "REPORTS_DOWNLOAD_FILE",
  REPORTS_UPLOAD_FILE = "REPORTS_UPLOAD_FILE",
  DISPUTES_GET = "DISPUTES_GET",
  DISPUTES_GET_ITEM = "DISPUTES_GET_ITEM",
  DISPUTES_CREATE = "DISPUTES_CREATE",
  DISPUTES_CLOSE = "DISPUTES_CLOSE",
  CHATS_GET_ALL = "CHATS_GET_ALL",
  CHATS_GET_BRIEFS = "CHATS_GET_BRIEFS",
  CHATS_WS_CONNECT = "CHATS_WS_CONNECT",
  MESSAGE_DOWNLOAD_FILE = "MESSAGE_DOWNLOAD_FILE",
  CHATS_GET_ITEM = "CHATS_GET_ITEM",
  CHATS_GET_ID = "CHATS_GET_ID",
  CHATS_UNREAD_MESSAGES = "CHATS_UNREAD_MESSAGES",
  GET_NICHES = "GET_NICHES",
  ORGANIZATIONS_GET = "ORGANIZATIONS_GET",
  ORGANIZATIONS_CREATE = "ORGANIZATIONS_CREATE",
  GAMES_PLATFORMS_GET = "GAMES_PLATFORMS_GET",
  GAMES_AGE_RESTRICTIONS_GET = "GAMES_AGE_RESTRICTIONS_GET"
}

export interface LoadingItem {
  id: string;
  type: LoadingStates;
}

@Injectable({
  providedIn: "root",
})
export class LoadingService {
  readonly StatesNames = LoadingStates;

  // Используем Map для более эффективного доступа по ключу
  private loadingStatesMap = signal<Map<LoadingStates, boolean>>(
    new Map(Object.values(LoadingStates).map(state => [state, false]))
  );

  // Храним только активные загрузки
  private activeLoadingsSignal = signal<LoadingItem[]>([]);

  // Публичные API
  // @ts-ignore
  readonly stateIsActive = computed((state: LoadingStates) =>
    this.loadingStatesMap().get(state) || false
  );

  readonly isAnyLoading = computed(() => this.activeLoadingsSignal().length > 0);

  // Метод для проверки конкретного состояния
  isStateLoading(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Метод для проверки нескольких состояний (любое из них активно)
  isAnyStateLoading(states: LoadingStates[]): boolean {
    return states.some(state => this.isStateLoading(state));
  }

  // Добавить загрузку и вернуть её ID
  startLoading(requestType = LoadingStates.UNKNOWN): string {
    const id = this.generateUUID();

    this.activeLoadingsSignal.update(loadings => [
      ...loadings,
      { id, type: requestType }
    ]);

    this.updateStateValue(requestType, true);

    return id;
  }

  // Завершить загрузку по ID
  finishLoading(id: string): void {
    const loadingItem = this.activeLoadingsSignal().find(item => item.id === id);

    if (!loadingItem) return;

    const type = loadingItem.type;

    // Удаляем загрузку
    this.activeLoadingsSignal.update(loadings =>
      loadings.filter(item => item.id !== id)
    );

    // Проверяем, остались ли другие загрузки этого типа
    const hasMoreOfSameType = this.activeLoadingsSignal().some(item => item.type === type);

    if (!hasMoreOfSameType) {
      this.updateStateValue(type, false);
    }
  }

  // Метод для прямого получения значения состояния
  getStateValue(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Вспомогательные методы
  private updateStateValue(state: LoadingStates, value: boolean): void {
    this.loadingStatesMap.update(map => {
      const newMap = new Map(map);
      newMap.set(state, value);
      return newMap;
    });
  }

  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
